import {
  Badge,
  Box,
  Button,
  Flex,
  FormLabel,
  Heading,
  Image,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setEssayAnswersRequest, getEssayAnswersRequest } from "../../../modules/admin-modules/exams/Actions";
import { getStudentExamFile } from "../../../modules/admin-modules/student-exams/Api";
import HomeworkPdfAnnotator from "./homework-pdf-annotator";

const QUESTION_BASE_URL = "https://asquera.com/asquera";
const PDF_BASE_URL = "https://api.asquera.com/storage/app/student_pdfs";
const googlePdfPreviewUrl = (url) =>
  `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(url)}`;

function toPdfBytes(data) {
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) {
    return new Uint8Array(data.buffer, data.byteOffset, data.byteLength).slice();
  }
  throw new Error("Unexpected PDF response");
}

async function fetchExamPdfBytes(filename) {
  const safeName = String(filename || "").replace(/^\//, "");
  try {
    const response = await getStudentExamFile(safeName);
    return toPdfBytes(response?.data);
  } catch (apiError) {
    const storageUrl = `${PDF_BASE_URL}/${encodeURIComponent(safeName).replace(/%2F/g, "/")}`;
    const response = await fetch(storageUrl);
    if (!response.ok) throw apiError;
    return new Uint8Array(await response.arrayBuffer());
  }
}

function buildEssayPayload(studentExamId, answers, annotatedFile) {
  if (!annotatedFile) {
    return { student_exam_id: studentExamId, answers };
  }
  const formData = new FormData();
  formData.append("student_exam_id", studentExamId);
  answers.forEach((answer, index) => {
    formData.append(`answers[${index}][id]`, answer.id);
    formData.append(`answers[${index}][mark]`, answer.mark);
    formData.append(`answers[${index}][wrong]`, answer.wrong);
    if (answer.comment) {
      formData.append(`answers[${index}][comment]`, answer.comment);
    }
  });
  formData.append("student_pdf", annotatedFile);
  return formData;
}

const CorrectStudentModal = ({ isOpen, onClose, studentGroup, examId }) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.exams.isLoading);
  const [marks, setMarks] = useState({});
  const [annotatedFile, setAnnotatedFile] = useState(null);
  const [pdfAnnotatorFailed, setPdfAnnotatorFailed] = useState(false);
  const annotatorSaveRef = useRef(null);

  useEffect(() => {
    if (!studentGroup?.answers) {
      setMarks({});
      setAnnotatedFile(null);
      setPdfAnnotatorFailed(false);
      return;
    }
    const next = {};
    studentGroup.answers.forEach((answer) => {
      next[answer.id] = {
        mark: answer.mark ?? "",
        wrong: String(answer.wrong ?? 0),
        comment: answer.comment ?? "",
      };
    });
    setMarks(next);
    setAnnotatedFile(null);
    setPdfAnnotatorFailed(false);
  }, [studentGroup]);

  const isCorrected =
    studentGroup?.status === "corrected" ||
    (studentGroup?.answers?.length > 0 &&
      studentGroup.answers.every(
        (answer) => answer?.mark != null && answer?.mark !== ""
      ));
  const studentPdf = studentGroup?.student_exam?.student_pdf;
  const pdfUrl = studentPdf ? `${PDF_BASE_URL}/${studentPdf}` : null;

  const updateMark = (answerId, patch) => {
    setMarks((prev) => ({
      ...prev,
      [answerId]: { ...prev[answerId], ...patch },
    }));
  };

  const handleSave = async () => {
    const answers = (studentGroup?.answers || []).map((answer) => {
      const comment = String(marks[answer.id]?.comment ?? "").trim();
      const payload = {
        id: answer.id,
        mark: Number(marks[answer.id]?.mark ?? 0),
        wrong: Number(marks[answer.id]?.wrong ?? 0),
      };
      if (comment) payload.comment = comment;
      return payload;
    });
    if (!studentGroup?.student_exam?.id) {
      toast.error("student exam not found");
      return;
    }
    if (answers.some((a) => a.mark === "" || Number.isNaN(a.mark))) {
      toast.error("أدخل درجة لكل سؤال");
      return;
    }

    let fileToUpload = annotatedFile;
    if (pdfUrl && !pdfAnnotatorFailed && annotatorSaveRef.current) {
      try {
        const saved = await annotatorSaveRef.current({ silent: true, allowEmpty: true });
        if (saved) {
          fileToUpload = saved;
          setAnnotatedFile(saved);
        }
      } catch {
        return;
      }
    }

    dispatch(
      setEssayAnswersRequest(
        buildEssayPayload(studentGroup.student_exam.id, answers, fileToUpload),
        () => {
          onClose();
          if (examId) {
            dispatch(getEssayAnswersRequest(examId));
          }
        }
      )
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent maxW="98vw" h="92vh" display="flex" flexDirection="column" my={3}>
        <ModalHeader color="main_1" flexShrink={0}>
          تصحيح إجابات {studentGroup?.name}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflow="hidden" pb={2} flex="1" minH={0} display="flex" flexDirection="column">
          <Flex flex="1" minH={0} h="100%" gap={4} dir="ltr">
            <Box
              flex="1.7"
              minW={0}
              h="100%"
              minH={0}
              display="flex"
              flexDirection="column"
              overflow="hidden"
            >
              {pdfUrl && !pdfAnnotatorFailed ? (
                <HomeworkPdfAnnotator
                  embedded
                  isOpen={isOpen}
                  filename={studentPdf}
                  fetchPdfBytes={fetchExamPdfBytes}
                  modalTitle="تعليق على ملف الطالب"
                  defaultFileName={studentPdf || "exam.pdf"}
                  loadErrorHint="تعذر تحميل ملف الطالب للتعليق. سيتم عرض المعاينة."
                  onSave={setAnnotatedFile}
                  saveRef={annotatorSaveRef}
                  readOnly={isCorrected}
                  onLoadError={() => setPdfAnnotatorFailed(true)}
                  toolbarActions={
                    <Flex align="center" gap={2} flexShrink={0}>
                      <Link href={pdfUrl} isExternal color="main_1" fontSize="12px" fontWeight="700">
                        فتح PDF
                      </Link>
                      {annotatedFile ? (
                        <Badge colorScheme="green" borderRadius="full" px={2}>
                          محفوظ
                        </Badge>
                      ) : null}
                    </Flex>
                  }
                />
              ) : pdfUrl ? (
                <Flex direction="column" h="100%" borderWidth="1px" borderColor="gray.200" borderRadius="12px" overflow="hidden">
                  <Flex justify="space-between" align="center" px={3} py={2} bg="white" borderBottomWidth="1px">
                    <Text fontWeight="600" fontSize="14px">
                      ملف الطالب PDF
                    </Text>
                    <Link href={pdfUrl} isExternal color="main_1" fontSize="13px">
                      فتح في تبويب جديد
                    </Link>
                  </Flex>
                  <Box flex={1}>
                    <iframe
                      title="student-pdf"
                      src={googlePdfPreviewUrl(pdfUrl)}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                    />
                  </Box>
                </Flex>
              ) : (
                <Box
                  p={4}
                  h="100%"
                  overflowY="auto"
                  dir="rtl"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="12px"
                  bg="gray.50"
                >
                  <Text fontWeight="700" color="main_1" mb={3}>
                    إجابات الطالب النصية
                  </Text>
                  <Stack spacing={4}>
                    {studentGroup?.answers?.map((answer, idx) => (
                      <Box
                        key={answer.id}
                        p={4}
                        bg="white"
                        borderRadius="12px"
                        borderWidth="1px"
                        borderColor="gray.200"
                      >
                        <Text fontWeight="600" mb={2}>
                          سؤال {idx + 1}
                        </Text>
                        <Box
                          fontSize="14px"
                          mb={3}
                          dangerouslySetInnerHTML={{
                            __html: answer?.question?.question || "",
                          }}
                        />
                        <Textarea
                          value={answer?.essay_answer || "لا توجد إجابة نصية"}
                          isReadOnly
                          minH="100px"
                          bg="#fafafa"
                        />
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>

            <Box
              flex="1"
              minW="280px"
              h="100%"
              overflowY="auto"
              p={4}
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="12px"
              dir="rtl"
            >
              <Heading size="sm" color="main_1" mb={4}>
                رصد الدرجات
              </Heading>
              {isCorrected && (
                <Text fontSize="14px" color="green.600" mb={3}>
                  تم التصحيح — لا يمكن تعديل الدرجات
                </Text>
              )}
              <Stack spacing={4}>
                {studentGroup?.answers?.map((answer, idx) => {
                  const maxMark = Number(answer?.question?.mark) || 1;
                  const values = marks[answer.id] || { mark: "", wrong: "0", comment: "" };
                  return (
                    <Box
                      key={answer.id}
                      p={4}
                      bg="#fafafa"
                      borderRadius="12px"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <Text fontWeight="700" mb={2}>
                        سؤال {idx + 1}
                      </Text>
                      <Box
                        fontSize="14px"
                        mb={3}
                        sx={{ "& p": { margin: 0 } }}
                        dangerouslySetInnerHTML={{
                          __html: answer?.question?.question || "",
                        }}
                      />
                      {answer?.question?.image && (
                        <Image
                          src={`${QUESTION_BASE_URL}/${answer.question.image}`}
                          alt={`question-${idx + 1}`}
                          maxH="160px"
                          objectFit="contain"
                          mb={3}
                          borderRadius="8px"
                        />
                      )}
                      <Text fontSize="13px" color="gray.600" mb={2}>
                        الدرجة الكاملة: {maxMark}
                      </Text>
                      <FormLabel fontSize="14px" mb={1}>
                        الدرجة
                      </FormLabel>
                      <Input
                        type="number"
                        min={0}
                        max={maxMark}
                        value={values.mark}
                        isReadOnly={isCorrected}
                        isDisabled={isCorrected}
                        onChange={(e) =>
                          updateMark(answer.id, { mark: e.target.value })
                        }
                        bg="white"
                        borderRadius="12px"
                        mb={3}
                      />
                      <FormLabel fontSize="14px" mb={1}>
                        الحالة
                      </FormLabel>
                      <Stack direction="row" spacing={5}>
                        <label>
                          <input
                            type="radio"
                            name={`wrong-${answer.id}`}
                            value="0"
                            checked={values.wrong === "0"}
                            disabled={isCorrected}
                            onChange={() => updateMark(answer.id, { wrong: "0" })}
                          />{" "}
                          صحيح
                        </label>
                        <label>
                          <input
                            type="radio"
                            name={`wrong-${answer.id}`}
                            value="1"
                            checked={values.wrong === "1"}
                            disabled={isCorrected}
                            onChange={() => updateMark(answer.id, { wrong: "1" })}
                          />{" "}
                          خطأ
                        </label>
                      </Stack>
                      <FormLabel fontSize="14px" mb={1} mt={3}>
                        تعليق (اختياري)
                      </FormLabel>
                      <Textarea
                        value={values.comment}
                        isReadOnly={isCorrected}
                        isDisabled={isCorrected}
                        onChange={(e) =>
                          updateMark(answer.id, { comment: e.target.value })
                        }
                        placeholder="اكتب تعليقاً على الإجابة"
                        bg="white"
                        borderRadius="12px"
                        minH="80px"
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter flexShrink={0}>
          <Button variant="ghost" ml={3} onClick={onClose}>
            إغلاق
          </Button>
          {!isCorrected && (
            <Button
              bg="main_1"
              color="white"
              isLoading={isLoading}
              onClick={handleSave}
            >
              حفظ الدرجات
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CorrectStudentModal;
